import React, { Suspense, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import {
	createBrowserRouter,
	createRoutesFromElements,
	Route,
	RouterProvider,
} from 'react-router-dom'

import { MainLayout } from '@/components'
import { IS_AI_AGENTS_PREVIEW_ENABLED, IS_PREVIEW_PAGES_ENABLED, IS_WIKI_ENABLED } from '@/config'
import { ROUTES } from '@/constants'
import { StereotypeDto } from '@/endpoints/models'
import { useAppDispatch, useAppStore } from '@/hooks'
import { AiAgentsLayout } from '@/pages/User/pages/AiAgents'
import { BranchesLayout } from '@/pages/User/pages/Branches'
import { DataCatalogIndex, DataCatalogLayout, CatalogTableDetail } from '@/pages/User/pages/DataCatalog'
import { MergeRequestsLayout } from '@/pages/User/pages/MergeRequests'
import { ReleasesLayout } from '@/pages/User/pages/Releases'
import { DeploymentDashboardLayout } from '@/pages/User/pages/DeploymentDashboard'
import { DeploymentPackagesLayout } from '@/pages/User/pages/DeploymentPackages'
import { SystemSettingsLayout } from '@/pages/User/pages/SystemSettings'
import { OntologyLayout } from '@/pages/User/pages/Ontology'
import { ReportsLayout } from '@/pages/User/pages/Reports'
import {
	PlaceholderPage,
	previewPages,
} from '@/pages/User/pages/PlaceholderPage'
import {
	readSelectedBranchNumericId,
	SELECTED_SYSTEM_STORAGE_KEY,
} from '@/pages/User/pages/Home/components/TreePanel/branchSelectionStorage'
import { loadNodeState } from '@/store/modules/node/actions'
import { updateSettings } from '@/store/modules/settings/actions'
import { loadStereotypes } from '@/store/modules/stereotype/actions'
import { loadTabState } from '@/store/modules/tab/actions'
import {
	EMPTY_TAB_SNAPSHOT,
	makeTabContextKey,
	resolveTabSnapshotForContext,
} from '@/store/modules/tab/helpers'
import { updateUserSession } from '@/store/modules/user/actions'

import { BootstrapProvider } from './context/BootstrapContext'

const Home = React.lazy(() =>
	import('@/pages/User/pages/Home').then((m) => ({ default: m.Home })),
)
const Settings = React.lazy(() =>
	import('@/pages/User/pages/Settings').then((m) => ({ default: m.Settings })),
)

const Wiki = React.lazy(() =>
	import('./pages/Wiki/Wiki').then((m) => ({ default: m.Wiki })),
)

export const AppRouter = () => {
	const dispatch = useAppDispatch()
	const userSession = useAppStore((state) => state.user.session)
	const [userLoaded, setUserLoaded] = useState(false)

	const router = createBrowserRouter(
		createRoutesFromElements(
			<Route path="/" element={<MainLayout />}>
				<Route
					index
					element={
						<Suspense fallback={null}>
							<Home />
						</Suspense>
					}
				/>
				<Route
					path={ROUTES.SETTINGS}
					element={
						<Suspense fallback={null}>
							<Settings />
						</Suspense>
					}
				/>
				<Route
					path={ROUTES.BRANCHES}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<BranchesLayout />
						</Box>
					}
				/>
				<Route
					path={ROUTES.BRANCHES_BRANCH}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<BranchesLayout />
						</Box>
					}
				/>
				<Route
					path={ROUTES.MERGE_REQUESTS}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<MergeRequestsLayout />
						</Box>
					}
				/>
				<Route
					path={ROUTES.MERGE_REQUESTS_MR}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<MergeRequestsLayout />
						</Box>
					}
				/>
				<Route
					path={ROUTES.DEPLOYMENT_PACKAGES}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<DeploymentPackagesLayout />
						</Box>
					}
				/>
				<Route
					path={ROUTES.DEPLOYMENT_PACKAGES_PACKAGE}
					element={
						<Box sx={{ height: '100%', minHeight: 0 }}>
							<DeploymentPackagesLayout />
						</Box>
					}
				/>
				{IS_PREVIEW_PAGES_ENABLED && (
					<Route
						path={ROUTES.DATA_CATALOG}
						element={
							<Box sx={{ height: '100%', minHeight: 0 }}>
								<DataCatalogLayout />
							</Box>
						}
					>
						<Route index element={<DataCatalogIndex />} />
						<Route path=":tableId" element={<CatalogTableDetail />} />
					</Route>
				)}
				{IS_PREVIEW_PAGES_ENABLED && (
					<>
						<Route
							path={ROUTES.REPORTS}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<ReportsLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.REPORTS_REPORT}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<ReportsLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.RELEASES}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<ReleasesLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.RELEASES_RELEASE}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<ReleasesLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.DEPLOYMENT_DASHBOARD}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<DeploymentDashboardLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.SYSTEM_SETTINGS}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<SystemSettingsLayout />
								</Box>
							}
						/>
						<Route
							path={ROUTES.ONTOLOGY}
							element={
								<Box sx={{ height: '100%', minHeight: 0 }}>
									<OntologyLayout />
								</Box>
							}
						/>
						{IS_AI_AGENTS_PREVIEW_ENABLED && (
							<Route
								path={ROUTES.AI_AGENTS}
								element={
									<Box sx={{ height: '100%', minHeight: 0 }}>
										<AiAgentsLayout />
									</Box>
								}
							/>
						)}
					</>
				)}
				{IS_PREVIEW_PAGES_ENABLED &&
					previewPages
						.filter(
							(page) =>
								page.path !== ROUTES.DATA_CATALOG &&
								page.path !== ROUTES.REPORTS &&
								page.path !== ROUTES.RELEASES &&
								page.path !== ROUTES.DEPLOYMENT_DASHBOARD &&
								page.path !== ROUTES.SYSTEM_SETTINGS &&
								page.path !== ROUTES.ONTOLOGY &&
								page.path !== ROUTES.AI_AGENTS,
						)
						.map((page) => (
							<Route
								key={page.path}
								path={page.path}
								element={
									<Box sx={{ height: '100%', minHeight: 0 }}>
										<PlaceholderPage
											icon={page.icon}
											titleKey={page.titleKey}
										/>
									</Box>
								}
							/>
						))}
				{IS_WIKI_ENABLED && (
					<Route
						path={ROUTES.WIKI}
						element={
							<Suspense fallback={null}>
								<Wiki />
							</Suspense>
						}
					/>
				)}
			</Route>,
		),
	)

	// Stereotypes load in background so we don't block first paint.
	useEffect(() => {
		dispatch(loadStereotypes(StereotypeDto.TypeEnum.TABLE_HISTORY))
	}, [dispatch])

	// Process user session (node state + selected tab; session already in Redux from App)
	useEffect(() => {
		const load = async () => {
			if (userSession) {
				dispatch(updateSettings(userSession.settings))
				const systemId = localStorage.getItem(SELECTED_SYSTEM_STORAGE_KEY)
				const contextKey = makeTabContextKey(
					systemId,
					readSelectedBranchNumericId(),
				)
				const storedTab = userSession.tab
				const snapshot = contextKey
					? resolveTabSnapshotForContext(storedTab, contextKey)
					: EMPTY_TAB_SNAPSHOT
				const tabSession = {
					...snapshot,
					contexts: storedTab?.contexts ?? {},
					contextKey,
				}
				await Promise.all([
					dispatch(loadNodeState(userSession.node)),
					dispatch(loadTabState(tabSession)),
				])
			}
			setUserLoaded(true)
		}

		load()
	}, [userSession, dispatch])

	// Auto-save user session: polls every 1s and only persists when the session snapshot changes
	useEffect(() => {
		let lastSaved = JSON.stringify(userSession)

		const interval = setInterval(async () => {
			lastSaved = ((await dispatch(updateUserSession(lastSaved))) || {})
				.context as string
		}, 1000)

		return () => clearInterval(interval)
	}, [dispatch, updateUserSession])

	return (
		<BootstrapProvider sessionProcessed={userLoaded}>
			<RouterProvider router={router} />
		</BootstrapProvider>
	)
}
